import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { getTranslation } from "../../translations/getTranslations";
import { changeMail, validateOldEmail, setNewMail, validateNewEmail } from "../../services/userService";
import { useNotification } from "../../notifications/Notifications";
import { useSelector, useDispatch } from "react-redux";
import { logoutAndClearData, setChangeEmailStep, setVerificationCodeTimestamp } from "../../redux/slices/authSlice";

const ChangeMailModal = ({ showModal, onClose }) => {
  const dispatch = useDispatch();
  const notify = useNotification();

  const verificationCodeTimestamp = useSelector(state => state.auth.user.verificationCodeTimestamp);
  const changeEmailStep = useSelector(state => state.auth.user.changeEmailStep);
  const language = useSelector(state => state.settings.language);

  const [oldEmail, setOldEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [newEmailVerificationCode, setNewEmailVerificationCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [confirmNewEmail, setConfirmNewEmail] = useState("");
  const [step, setStep] = useState(1);
  const [minutesLeft, setMinutesLeft] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const translation = key => getTranslation("changeEmailModal", key, language);

  useEffect(() => {
    setVerificationCode("");
    setConfirmNewEmail("");
    decideStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  useEffect(() => {
    if (verificationCodeTimestamp <= 0) {
      return;
    }

    const updateCountdown = () => {
      const currentTime = Date.now();
      const difference = currentTime - verificationCodeTimestamp;

      if (difference >= 15 * 60 * 1000) {
        setMinutesLeft(0);
        setSecondsLeft(0);
        dispatch(setVerificationCodeTimestamp(0));
        dispatch(setChangeEmailStep(1));

        setOldEmail("");
        setNewEmail("");
        setStep(1);

        return;
      }

      const remainingSeconds = Math.max(0, (15 * 60 * 1000 - difference) / 1000);

      setMinutesLeft(Math.floor(remainingSeconds / 60));
      setSecondsLeft(Math.floor(remainingSeconds % 60));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verificationCodeTimestamp]);

  const decideStep = () => {
    if (changeEmailStep === 2) {
      setStep(2);
    } else if (changeEmailStep === 3) {
      setStep(3);
    } else if (changeEmailStep === 4) {
      setStep(4);
    } else {
      setStep(1);
      setMinutesLeft(0);
      setSecondsLeft(0);
      dispatch(setVerificationCodeTimestamp(0));
      dispatch(setChangeEmailStep(1));
    }
  };

  const handleEmailSubmit = async () => {
    if (!oldEmail) {
      notify("error", translation("_requiredEmail"));
      return;
    }

    if (!emailRegex.test(oldEmail)) {
      notify("error", translation("_invalidProvidedEmail"));
      return;
    }

    try {
      const response = await changeMail(oldEmail);
      const successMessage = response.trim().replace(/^"+|"+$/g, "");
      notify("success", translation(successMessage));

      dispatch(setVerificationCodeTimestamp(Date.now()));
      dispatch(setChangeEmailStep(2));
      setStep(2);
    } catch (error) {
      const errorMessage = error.message.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorMessage));
    }
  };

  const handleCodeVerificationSubmit = async () => {
    if (!verificationCode) {
      notify("error", translation("_requiredCode"));
      return;
    }

    try {
      await validateOldEmail(verificationCode);

      dispatch(setVerificationCodeTimestamp(0));
      dispatch(setChangeEmailStep(3));
      setStep(3);
    } catch (error) {
      setVerificationCode("");
      notify("error", translation("_invalidCode"));
    }
  };

  const handleNewEmailSubmit = async () => {
    if (!newEmail || !confirmNewEmail) {
      notify("error", translation("_requiredNewEmail"));
      return;
    }

    if (!emailRegex.test(newEmail) || !emailRegex.test(confirmNewEmail)) {
      notify("error", translation("_invalidProvidedEmail"));
      return;
    }

    if (newEmail !== confirmNewEmail) {
      notify("error", translation("_emailsMismatch"));
      return;
    }

    try {
      const resposne = await setNewMail(newEmail);
      // notify("success", translation("_verificationLinkSent"));
      const successMessage = resposne.trim().replace(/^"+|"+$/g, "");
      notify("success", translation(successMessage));

      dispatch(setVerificationCodeTimestamp(Date.now()));
      dispatch(setChangeEmailStep(4));
      setStep(4);
    } catch (error) {
      const errorMessage = error.message.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorMessage));
    }
  };

  const handleNewEmailVerificationSubmit = async () => {
    if (!newEmailVerificationCode) {
      notify("error", translation("_requiredCode"));
      return;
    }

    try {
      const resposne = await validateNewEmail(newEmailVerificationCode);
      // notify("success", translation("_emailUpdated"));
      const successMessage = resposne.trim().replace(/^"+|"+$/g, "");
      notify("success", translation(successMessage));
      onClose();

      dispatch(setVerificationCodeTimestamp(0));
      dispatch(setChangeEmailStep(1));
      dispatch(logoutAndClearData());
    } catch (error) {
      setVerificationCode("");
      const errorMessage = error.message.trim().replace(/^"+|"+$/g, "");
      notify("error", translation(errorMessage));
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div
        className={`relative w-[500px] rounded bg-backgroundPrimary p-6 text-center text-lg text-textPrimary shadow-lg`}
      >
        <button
          className={`absolute right-1 top-0 p-2 text-xl text-buttonCancel hover:text-buttonCancelHover`}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="mb-6 text-2xl font-semibold">{translation("_changeEmailModalTitle")}</h2>
        {step === 1 && (
          <>
            <p className="mb-4 text-base">{translation("_enterOldEmailDesc")}</p>
            <input
              type="email"
              className={`w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_oldEmailPlaceholder")}
              value={oldEmail}
              onChange={e => setOldEmail(e.target.value)}
            />
            <p className="my-2 h-5 text-sm text-red-500"></p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handleEmailSubmit}
            >
              {translation("_sendVerificationCode")}
            </button>
          </>
        )}
        {step === 2 && (
          <>
            <p className="mb-4 text-base">{translation("_verificationCodeDesc")}</p>
            <input
              type="email"
              className={`mb-3 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              value={oldEmail}
              disabled={true}
            />
            <input
              type="text"
              className={`mb-3 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_verificationCodePlaceholder")}
              value={verificationCode}
              onChange={e => setVerificationCode(e.target.value)}
            />
            <p className="my-2 h-5 text-sm text-red-500"></p>
            <p className="my-2 h-5 text-sm">
              {translation("_timeLeft")}: {minutesLeft.toString().padStart(2, 0)}:
              {secondsLeft.toString().padStart(2, 0)}
            </p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handleCodeVerificationSubmit}
            >
              {translation("_verifyCode")}
            </button>
          </>
        )}
        {step === 3 && (
          <>
            <p className="mb-4 text-base">{translation("_newEmailDesc")}</p>
            <input
              type="email"
              className={`w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_newEmailPlaceholder")}
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
            />
            <input
              type="email"
              className={`mt-2 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_confirmNewEmailPlaceholder")}
              value={confirmNewEmail}
              onChange={e => setConfirmNewEmail(e.target.value)}
            />
            <p className="my-2 h-5 text-sm text-red-500"></p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handleNewEmailSubmit}
            >
              {translation("_submit")}
            </button>
          </>
        )}
        {step === 4 && (
          <>
            <p className="mb-4 text-base">{translation("_verificationCodeDesc")}</p>
            <input
              type="email"
              className={`w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              value={newEmail}
              disabled={true}
            />
            <input
              type="text"
              className={`mt-2 w-full rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
              placeholder={translation("_verificationCodePlaceholder")}
              value={newEmailVerificationCode}
              onChange={e => setNewEmailVerificationCode(e.target.value)}
            />
            <p className="my-2 h-5 text-sm text-red-500"></p>
            <p className="my-2 h-5 text-sm">
              {translation("_timeLeft")}: {minutesLeft.toString().padStart(2, 0)}:
              {secondsLeft.toString().padStart(2, 0)}
            </p>

            <button
              className={`w-full cursor-pointer rounded bg-buttonSubmit p-3 text-base text-buttonText hover:bg-buttonSubmitHover`}
              onClick={handleNewEmailVerificationSubmit}
            >
              {translation("_submit")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ChangeMailModal;
